import { useRef, useEffect } from "react";

import type { CustomerSearchResult } from "~/types";
import { cn } from "~/utils";

interface CustomerResultsListProps {
  results: CustomerSearchResult[];
  highlightedIndex: number;
  onHighlight: (index: number) => void;
  onSelect: (customer: CustomerSearchResult) => void;
}

/** Highlightable customer rows, shared by CustomerPicker and CustomerSearchModal. */
export function CustomerResultsList({
  results,
  highlightedIndex,
  onHighlight,
  onSelect,
}: CustomerResultsListProps) {
  const listRef = useRef<HTMLDivElement>(null);

  // Keep the highlighted row in view as arrow keys move past the visible edge.
  useEffect(() => {
    listRef.current
      ?.querySelector(`[data-index="${highlightedIndex}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex]);

  return (
    <div ref={listRef}>
      {results.map((customer, index) => (
        <button
          key={customer.id}
          data-index={index}
          type="button"
          onClick={() => onSelect(customer)}
          onMouseEnter={() => onHighlight(index)}
          className={cn(
            "flex w-full flex-col px-3 py-2 text-left text-sm transition-colors",
            index === highlightedIndex
              ? "bg-blue-50 text-blue-900"
              : "hover:bg-gray-50",
          )}
        >
          <span className="font-medium">{customer.name}</span>
          <span className="font-mono text-xs text-gray-500">{customer.id}</span>
        </button>
      ))}
    </div>
  );
}
